import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ThemeContext = createContext(undefined);

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => {
    // Check localStorage first
    const savedTheme = localStorage.getItem('viralla-theme');
    if (savedTheme && savedTheme !== 'system') {
      return savedTheme;
    }
    // Check system preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  });
  
  const [syncedWithBackend, setSyncedWithBackend] = useState(false);

  // Apply theme to document
  const applyTheme = useCallback((newTheme) => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    
    let effectiveTheme = newTheme;
    if (newTheme === 'system') {
      effectiveTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    
    root.classList.add(effectiveTheme);
    return effectiveTheme;
  }, []);

  // Sync theme preference to backend
  const syncThemeToBackend = useCallback(async (newTheme) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;
      
      await axios.put(`${API}/users/preferences`, 
        { theme: newTheme },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Failed to sync theme to backend:', error);
    }
  }, []);

  // Set theme and optionally sync to backend
  const setTheme = useCallback((newTheme, syncToBackend = true) => {
    const effectiveTheme = applyTheme(newTheme);
    setThemeState(effectiveTheme);
    localStorage.setItem('viralla-theme', newTheme);
    
    if (syncToBackend) {
      syncThemeToBackend(newTheme);
    }
  }, [applyTheme, syncThemeToBackend]);

  // Load theme from user preferences on mount
  useEffect(() => {
    const loadUserPreferences = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) return;
        
        const response = await axios.get(`${API}/users/preferences`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const userTheme = response.data.theme;
        if (userTheme && userTheme !== 'system') {
          setTheme(userTheme, false); // Don't sync back, just apply
        } else if (userTheme === 'system') {
          // Apply system preference
          const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
          applyTheme(systemTheme);
          setThemeState(systemTheme);
        }
        
        setSyncedWithBackend(true);
      } catch (error) {
        // User not logged in or preferences not available
        console.log('Could not load user theme preferences');
      }
    };
    
    loadUserPreferences();
  }, [setTheme, applyTheme]);

  // Apply theme on change
  useEffect(() => {
    applyTheme(theme);
  }, [theme, applyTheme]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      // Only auto-switch if theme is set to 'system'
      const savedTheme = localStorage.getItem('viralla-theme');
      if (savedTheme === 'system' || !savedTheme) {
        const newTheme = e.matches ? 'dark' : 'light';
        applyTheme(newTheme);
        setThemeState(newTheme);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [applyTheme]);

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  }, [theme, setTheme]);

  const setLightTheme = useCallback(() => setTheme('light'), [setTheme]);
  const setDarkTheme = useCallback(() => setTheme('dark'), [setTheme]);
  const setSystemTheme = useCallback(() => {
    const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    localStorage.setItem('viralla-theme', 'system');
    applyTheme(systemTheme);
    setThemeState(systemTheme);
    syncThemeToBackend('system');
  }, [applyTheme, syncThemeToBackend]);

  const value = {
    theme,
    isDark: theme === 'dark',
    isLight: theme === 'light',
    toggleTheme,
    setLightTheme,
    setDarkTheme,
    setSystemTheme,
    setTheme,
    syncedWithBackend,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
